import { useState } from 'react';
import { Link } from 'react-router-dom';
import {
  Boxes,
  Briefcase,
  Camera,
  ChevronRight,
  ClipboardList,
  Inbox,
  Mic2,
  Plus,
  Send,
  Settings,
  Tag,
  Trash2,
} from 'lucide-react';
import {
  Avatar,
  Badge,
  Button,
  Card,
  CardBody,
  CardFooter,
  CardHeader,
  Checkbox,
  EmptyState,
  HelperText,
  Input,
  Modal,
  NavBar,
  Radio,
  RadioGroup,
  Select,
  Sidebar,
  Spinner,
  Switch,
  Tab,
  TabList,
  TabPanel,
  Table,
  Tabs,
  Textarea,
  Tooltip,
  useToast,
} from '../../components/index.js';
import type { TableColumn } from '../../components/index.js';

const Section = ({ title, description, children }: { title: string; description?: string; children: React.ReactNode }) => (
  <section className="py-10 border-b border-border-subtle last:border-0">
    <header className="mb-6">
      <h2 className="text-2xl font-semibold text-text-primary">{title}</h2>
      {description && <p className="mt-1 text-base text-text-secondary max-w-3xl">{description}</p>}
    </header>
    {children}
  </section>
);

const Row = ({ children, className }: { children: React.ReactNode; className?: string }) => (
  <div className={`flex flex-wrap items-start gap-4 ${className ?? ''}`}>{children}</div>
);

interface JobRow {
  id: string;
  business: string;
  city: string;
  fee: number;
  status: 'draft' | 'active' | 'complete';
  inspected: string;
}

const sampleRows: JobRow[] = [
  { id: '1', business: 'Pacific Coffee Roasters', city: 'Vancouver', fee: 1800, status: 'active', inspected: '2026-04-12' },
  { id: '2', business: 'Cascadia Bakery', city: 'Burnaby', fee: 1500, status: 'draft', inspected: '2026-04-22' },
  { id: '3', business: "Mariner's Pub", city: 'Richmond', fee: 2400, status: 'complete', inspected: '2026-03-30' },
  { id: '4', business: 'Fern Salon', city: 'Surrey', fee: 950, status: 'active', inspected: '2026-04-18' },
];

export function InternalDesign() {
  return (
    <div className="min-h-screen flex flex-col">
      <NavBar
        userMenu={
          <div className="flex items-center gap-3">
            <span className="text-sm text-text-secondary hidden sm:inline">Adam Thompson</span>
            <Avatar name="Adam Thompson" size="md" />
          </div>
        }
      />
      <div className="flex flex-1">
        <Sidebar
          sections={[
            {
              title: 'Workspace',
              items: [
                { label: 'Overview', to: '/internal/design#overview', icon: Boxes },
                { label: 'Jobs', to: '/internal/design#jobs', icon: Briefcase, badge: <Badge variant="accent">12</Badge> },
              ],
            },
            {
              title: 'Tools',
              items: [
                { label: 'Dictation (STT)', to: '/internal/design#stt', icon: Mic2, badge: 'Soon' },
                { label: 'Pricing', to: '/internal/design#pricing', icon: Tag, badge: 'Soon' },
                { label: 'Asset Editor', to: '/internal/design#editor', icon: ClipboardList, badge: 'Soon' },
                { label: 'Photo Triage', to: '/internal/design#photos', icon: Camera, badge: 'Soon' },
              ],
            },
            {
              title: 'Workspace',
              items: [
                { label: 'Settings', to: '/internal/design#settings', icon: Settings },
              ],
            },
          ]}
          footer={
            <Link to="/" className="text-sm text-text-secondary hover:text-text-primary">
              ← Back to portal
            </Link>
          }
        />
        <main className="flex-1 overflow-auto">
          <div className="max-w-6xl mx-auto px-8 py-10">
            <header className="mb-10">
              <p className="text-sm font-medium text-accent-base">Internal · Design system v0.1</p>
              <h1 className="mt-1 text-3xl font-bold text-text-primary">Components &amp; tokens</h1>
              <p className="mt-2 text-base text-text-secondary max-w-2xl">
                Reference for the TAS Portal dark theme. Built for clarity, calm density, and Greg-friendly readability (17px body, AAA contrast).
              </p>
            </header>

            <Tokens />
            <Buttons />
            <Forms />
            <Selection />
            <Surfaces />
            <Feedback />
            <NavigationDemo />
            <DataDemo rows={sampleRows} />
          </div>
        </main>
      </div>
    </div>
  );
}

function Tokens() {
  const swatches = [
    { name: 'bg-base', cls: 'bg-bg-base', hex: '#0B0F19' },
    { name: 'bg-surface-1', cls: 'bg-bg-surface-1', hex: '#131825' },
    { name: 'bg-surface-2', cls: 'bg-bg-surface-2', hex: '#1B2030' },
    { name: 'bg-surface-3', cls: 'bg-bg-surface-3', hex: '#252B3D' },
    { name: 'accent-base', cls: 'bg-accent-base', hex: '#5B8DEF' },
    { name: 'accent-hover', cls: 'bg-accent-hover', hex: '#7AA3F2' },
    { name: 'accent-muted', cls: 'bg-accent-muted', hex: '#2A4574' },
    { name: 'success', cls: 'bg-success', hex: '#4ADE80' },
    { name: 'warning', cls: 'bg-warning', hex: '#FBBF24' },
    { name: 'danger', cls: 'bg-danger', hex: '#F87171' },
    { name: 'info', cls: 'bg-info', hex: '#60A5FA' },
  ];
  const types = [
    { token: 'text-4xl', label: 'Display heading', cls: 'text-4xl font-bold' },
    { token: 'text-3xl', label: 'Page heading', cls: 'text-3xl font-bold' },
    { token: 'text-2xl', label: 'Section heading', cls: 'text-2xl font-semibold' },
    { token: 'text-xl', label: 'Card title', cls: 'text-xl font-semibold' },
    { token: 'text-lg', label: 'Emphasized body', cls: 'text-lg font-medium' },
    { token: 'text-base', label: 'Body — 17px (default)', cls: 'text-base' },
    { token: 'text-sm', label: 'Secondary label', cls: 'text-sm text-text-secondary' },
    { token: 'text-xs', label: 'Tiny status (use sparingly)', cls: 'text-xs uppercase tracking-wider text-text-secondary' },
  ];
  return (
    <Section title="Tokens" description="Colors anchor surfaces; type scale anchors hierarchy. Greg-friendly defaults.">
      <div className="grid gap-4 sm:grid-cols-2">
        <Card>
          <CardHeader title="Surfaces &amp; status" />
          <CardBody className="grid grid-cols-3 gap-3">
            {swatches.map((s) => (
              <div key={s.name} className="flex flex-col gap-1">
                <span className={`block h-12 rounded-md border border-border-subtle ${s.cls}`} aria-hidden />
                <span className="text-xs font-mono text-text-primary">{s.name}</span>
                <span className="text-[11px] font-mono text-text-secondary">{s.hex}</span>
              </div>
            ))}
          </CardBody>
        </Card>
        <Card>
          <CardHeader title="Type scale" description="Inter Variable; mono is JetBrains Mono Variable." />
          <CardBody className="space-y-3">
            {types.map((t) => (
              <div key={t.token} className="flex items-baseline justify-between gap-4 border-b border-border-subtle pb-2 last:border-0">
                <span className={t.cls}>{t.label}</span>
                <span className="font-mono text-xs text-text-secondary">{t.token}</span>
              </div>
            ))}
          </CardBody>
        </Card>
      </div>
    </Section>
  );
}

function Buttons() {
  return (
    <Section title="Buttons" description="Primary, secondary, ghost, danger × small / medium / large. Always 44px+ touch target.">
      <div className="space-y-4">
        <Row>
          <Button>Primary</Button>
          <Button variant="secondary">Secondary</Button>
          <Button variant="ghost">Ghost</Button>
          <Button variant="danger">Danger</Button>
        </Row>
        <Row>
          <Button size="sm">Small</Button>
          <Button size="md">Medium</Button>
          <Button size="lg">Large</Button>
        </Row>
        <Row>
          <Button leftIcon={<Plus className="size-4" aria-hidden />}>New job</Button>
          <Button variant="secondary" rightIcon={<ChevronRight className="size-4" aria-hidden />}>
            Continue
          </Button>
          <Button loading>Saving</Button>
          <Button disabled>Disabled</Button>
        </Row>
      </div>
    </Section>
  );
}

function Forms() {
  return (
    <Section title="Inputs" description="Labels above, error text in danger, helper text in secondary. 48px input height.">
      <div className="grid gap-6 md:grid-cols-2">
        <Input label="Business name" placeholder="Pacific Coffee Roasters" helperText="Visible on the appraisal cover sheet." />
        <Input
          label="Email"
          type="email"
          defaultValue="not-an-email"
          errorText="Looks like an invalid email."
          leftAdornment={<Send className="size-4" aria-hidden />}
        />
        <Select label="Valuation type" defaultValue="going-concern" helperText="Affects how depreciation is applied.">
          <option value="going-concern">Going concern</option>
          <option value="orderly-liquidation">Orderly liquidation</option>
          <option value="forced-liquidation">Forced liquidation</option>
        </Select>
        <Textarea label="Inspection notes" placeholder="Roof in good shape, walk-in cooler 12×8 ft…" maxLength={500} showCount />
      </div>
    </Section>
  );
}

function Selection() {
  const [plan, setPlan] = useState('owner');
  const [enabled, setEnabled] = useState(true);
  return (
    <Section title="Selection" description="Switches, checkboxes, radios — always pair with a label.">
      <div className="grid gap-6 md:grid-cols-3">
        <Card>
          <CardHeader title="Switches" />
          <CardBody className="flex flex-col gap-3">
            <Switch id="sw-1" label="Email notifications" description="Daily summary at 9am" checked={enabled} onChange={(e) => setEnabled(e.currentTarget.checked)} />
            <Switch id="sw-2" label="Auto-save" description="Saves drafts every 60s" defaultChecked />
            <Switch id="sw-3" label="Locked setting" disabled />
          </CardBody>
        </Card>
        <Card>
          <CardHeader title="Checkboxes" />
          <CardBody className="flex flex-col gap-1">
            <Checkbox id="cb-1" label="Include legal disclaimer" defaultChecked />
            <Checkbox id="cb-2" label="Mark as preliminary" description="Adds a watermark to the PDF" />
            <Checkbox id="cb-3" label="Frozen entry" disabled />
          </CardBody>
        </Card>
        <Card>
          <CardHeader title="Radio group" />
          <CardBody>
            <RadioGroup name="role" value={plan} onChange={setPlan} label="Member role" description="Choose the access level.">
              <Radio value="owner" label="Owner" description="Full control" />
              <Radio value="admin" label="Admin" description="Invite + edit" />
              <Radio value="member" label="Member" description="Read + use tools" />
              <Radio value="viewer" label="Viewer" description="Read-only" />
            </RadioGroup>
          </CardBody>
        </Card>
      </div>
    </Section>
  );
}

function Surfaces() {
  return (
    <Section title="Surfaces" description="Cards stack on bg-base. Headers carry titles; bodies carry content; footers carry actions.">
      <div className="grid gap-6 md:grid-cols-2">
        <Card>
          <CardHeader title="Workspace overview" description="Timeline Asset Services" actions={<Badge variant="success" dot>Active</Badge>} />
          <CardBody>
            <p className="text-base text-text-secondary">12 jobs · 4 active inspections this month.</p>
          </CardBody>
          <CardFooter>
            <Button variant="ghost">Cancel</Button>
            <Button>Edit settings</Button>
          </CardFooter>
        </Card>
        <Card>
          <CardHeader title="Avatars &amp; badges" />
          <CardBody className="flex flex-col gap-4">
            <Row>
              <Avatar name="Adam Thompson" size="sm" />
              <Avatar name="Greg Schadenberg" size="md" />
              <Avatar name="Ben Carter" size="lg" />
              <Avatar name="Quinn River" size="xl" />
            </Row>
            <Row>
              <Badge>Neutral</Badge>
              <Badge variant="success" dot>Active</Badge>
              <Badge variant="warning" dot>Draft</Badge>
              <Badge variant="danger">Failed</Badge>
              <Badge variant="info">Info</Badge>
              <Badge variant="accent">Beta</Badge>
            </Row>
          </CardBody>
        </Card>
      </div>
    </Section>
  );
}

function Feedback() {
  const { toast } = useToast();
  const [open, setOpen] = useState(false);
  return (
    <Section title="Feedback" description="Modals confirm; toasts inform. Spinners hold space.">
      <Row className="items-center">
        <Button onClick={() => setOpen(true)} variant="danger" leftIcon={<Trash2 className="size-4" aria-hidden />}>
          Delete workspace
        </Button>
        <Button variant="secondary" onClick={() => toast({ title: 'Saved', description: 'Settings updated.', variant: 'success' })}>
          Show success toast
        </Button>
        <Button variant="secondary" onClick={() => toast({ title: 'Heads up', description: 'Greg has not been invited yet.', variant: 'warning' })}>
          Show warning toast
        </Button>
        <Button variant="secondary" onClick={() => toast({ title: 'Could not save', description: 'Network unreachable. Try again.', variant: 'danger' })}>
          Show error toast
        </Button>
        <Tooltip content="Coming in next phase">
          <Button variant="secondary" disabled>
            New job
          </Button>
        </Tooltip>
        <Spinner size="md" />
      </Row>
      <Modal
        open={open}
        onClose={() => setOpen(false)}
        title="Delete workspace?"
        description="This permanently deletes the Timeline Asset Services workspace and all jobs inside it. This cannot be undone."
        footer={
          <>
            <Button variant="ghost" onClick={() => setOpen(false)}>Cancel</Button>
            <Button variant="danger" onClick={() => { setOpen(false); toast({ title: 'Workspace deleted', variant: 'success' }); }}>
              Delete
            </Button>
          </>
        }
      >
        <p className="text-base text-text-secondary">Type the workspace name to confirm.</p>
        <Input className="mt-3" placeholder="Timeline Asset Services" />
      </Modal>
    </Section>
  );
}

function NavigationDemo() {
  return (
    <Section title="Tabs" description="Keyboard-navigable. Arrow keys move between tabs.">
      <Tabs defaultValue="overview">
        <TabList>
          <Tab value="overview">Overview</Tab>
          <Tab value="members">Members</Tab>
          <Tab value="settings">Settings</Tab>
          <Tab value="audit" disabled>Audit log</Tab>
        </TabList>
        <TabPanel value="overview">
          <p className="text-base text-text-secondary">Job summary, KPIs, recent activity.</p>
        </TabPanel>
        <TabPanel value="members">
          <p className="text-base text-text-secondary">Invite + role management.</p>
        </TabPanel>
        <TabPanel value="settings">
          <p className="text-base text-text-secondary">Workspace name, slug, branding.</p>
        </TabPanel>
      </Tabs>
    </Section>
  );
}

function DataDemo({ rows }: { rows: JobRow[] }) {
  const [loading, setLoading] = useState(false);
  const [empty, setEmpty] = useState(false);
  const cols: TableColumn<JobRow>[] = [
    { id: 'business', header: 'Business', cell: (r) => r.business, sortable: true, sortValue: (r) => r.business },
    { id: 'city', header: 'City', cell: (r) => r.city, sortable: true, sortValue: (r) => r.city },
    { id: 'fee', header: 'Fee', align: 'right', cell: (r) => `$${r.fee.toLocaleString()}`, sortable: true, sortValue: (r) => r.fee },
    {
      id: 'status',
      header: 'Status',
      cell: (r) => {
        const variant = r.status === 'active' ? 'success' : r.status === 'draft' ? 'warning' : 'neutral';
        return <Badge variant={variant} dot>{r.status}</Badge>;
      },
    },
    { id: 'inspected', header: 'Inspected', cell: (r) => r.inspected, sortable: true, sortValue: (r) => r.inspected },
  ];
  return (
    <Section title="Tables" description="Sortable headers (click), loading skeletons, empty states.">
      <div className="flex flex-wrap items-center gap-3 mb-4">
        <Button variant="secondary" size="sm" onClick={() => { setLoading(true); setEmpty(false); window.setTimeout(() => setLoading(false), 1200); }}>
          Toggle loading
        </Button>
        <Button variant="secondary" size="sm" onClick={() => { setEmpty((e) => !e); setLoading(false); }}>
          Toggle empty
        </Button>
        <HelperText>Click a column header to sort.</HelperText>
      </div>
      <Table<JobRow>
        columns={cols}
        rows={empty ? [] : rows}
        rowKey={(r) => r.id}
        loading={loading}
        emptyState={<EmptyState icon={Inbox} title="No jobs yet" description="When jobs are created they will appear here." />}
        caption="Recent jobs"
      />
    </Section>
  );
}


